import { AlertTriangle, Check, ExternalLink, RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button';
import { useInvoices, useItflowSettings, useRetryInvoice } from './useItflow';
import type { Invoice } from './types';

const STATE_LABELS: Record<Invoice['state'], string> = {
  pending: 'Queued',
  invoice_created: 'Posting lines…',
  partial: 'Incomplete',
  complete: 'Sent to ITFlow',
  failed: 'Failed',
};

function invoiceNumber(invoice: Invoice): string {
  if (invoice.itflow_invoice_number === null) return 'Not yet created';
  return `${invoice.itflow_invoice_prefix ?? ''}${invoice.itflow_invoice_number}`;
}

/**
 * What has been handed to ITFlow, and what got stuck on the way.
 *
 * A half-posted invoice looks finished in ITFlow while missing lines, so
 * "4 of 7" is shown plainly rather than rolled into a generic error. Retry
 * resumes: it posts only what is missing and never duplicates what landed.
 */
export function InvoiceHistory() {
  const settings = useItflowSettings();
  const invoices = useInvoices(Boolean(settings.data?.configured));
  const retry = useRetryInvoice();

  if (invoices.isLoading) {
    return <div className="h-32 animate-pulse rounded-lg bg-surface-2" aria-hidden />;
  }

  const rows = invoices.data ?? [];
  if (rows.length === 0) {
    return (
      <p className="text-small text-text-tertiary">No invoices generated yet.</p>
    );
  }

  const baseUrl = settings.data?.base_url;

  return (
    <ul className="flex flex-col gap-2">
      {rows.map((invoice) => {
        const stuck = invoice.state === 'partial' || invoice.state === 'failed';
        const done = invoice.state === 'complete';

        return (
          <li
            key={invoice.id}
            className="flex flex-col gap-2 rounded-lg border border-border bg-surface-1 p-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="flex items-center gap-2 text-body text-text">
                  {invoiceNumber(invoice)}
                  {done && <Check size={14} strokeWidth={2} className="text-success" aria-hidden />}
                  {stuck && (
                    <AlertTriangle size={14} strokeWidth={1.5} className="text-warning" aria-hidden />
                  )}
                </span>
                <span className="text-small text-text-tertiary">
                  {STATE_LABELS[invoice.state]} · {invoice.billed_count} of{' '}
                  {invoice.line_count} line{invoice.line_count === 1 ? '' : 's'} ·{' '}
                  {invoice.currency} {invoice.expected_total}
                </span>
              </div>

              <div className="flex shrink-0 items-center gap-2">
                {stuck && (
                  <Button onClick={() => retry.mutate(invoice.id)} loading={retry.isPending}>
                    <RefreshCw size={14} strokeWidth={1.5} aria-hidden />
                    Retry
                  </Button>
                )}
                {baseUrl && invoice.itflow_invoice_id !== null && (
                  <a
                    href={`${baseUrl}/invoice.php?invoice_id=${invoice.itflow_invoice_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-touch items-center gap-1.5 rounded px-2 text-small text-text-secondary hover:text-text"
                  >
                    <ExternalLink size={14} strokeWidth={1.5} aria-hidden />
                    Open
                  </a>
                )}
              </div>
            </div>

            {invoice.last_error && (
              <p className="text-small text-warning">{invoice.last_error}</p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
